import React, { useEffect, useState } from "react";
import { withAuthenticationRequired } from "../context/AuthContext";
import Loading from "../components/Loading";
import {
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { AddCircleOutline } from "@mui/icons-material";
import {
  COMPARSAS,
  HUMORISTAS,
  MURGAS,
  PARODISTAS,
  REVISTAS,
  estadoConcursoDescripcion,
  estadosConcurso
} from "../utils/constants";

import { ConjuntoConcursoListItem } from "./ConjuntoConcursoListItem";
import { HallazgosValidacionList } from "../components/HallazgosValidacionList";
import { BarraProgresoConcurso } from "../components/BarraProgresoConcurso";
import { DateField } from "../components/DateField";
import { QuantityStepper } from "../components/QuantityStepper";
import { showErrorMessage, showSuccessMessage } from "../redux/appSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BiCalendarAlt, BiErrorAlt, BiCheckShield } from "react-icons/bi";
import { FaFlagCheckered } from "react-icons/fa";
import { useRequestHandler } from "../utils/useRequestHandler";
import Servicios from "../services";
import { GiPodium } from "react-icons/gi";
import { FiSettings } from "react-icons/fi";
import { formatearFechaHoraYYYYMMDDHHMMSS } from "../utils/dates";

// Las 5 categorías concursantes se repetían idénticas 5 veces (estado, campos
// del form, columna del grid de alta, `<QuantityStepper>` ×2, listado con
// barra de progreso) — este config-array las colapsa a un solo lugar que se
// recorre, en vez de mantener 5 copias en paralelo.
const CATEGORIAS = [
  { key: "murgas", label: "Murgas", clave: MURGAS },
  { key: "parodistas", label: "Parodistas", clave: PARODISTAS },
  { key: "humoristas", label: "Humoristas", clave: HUMORISTAS },
  { key: "revistas", label: "Revistas", clave: REVISTAS },
  { key: "comparsas", label: "Comparsas", clave: COMPARSAS }
];

const CANTIDADES_INICIALES = {
  murgas: 21,
  parodistas: 5,
  humoristas: 4,
  revistas: 4,
  comparsas: 5
};

const CANTIDADES_LIGUILLA_INICIALES = {
  murgas: 10,
  parodistas: 4,
  humoristas: 3,
  revistas: 3,
  comparsas: 4
};

const capitalizar = texto => texto.charAt(0).toUpperCase() + texto.slice(1);

const conjuntosVacios = () =>
  Object.fromEntries(CATEGORIAS.map(c => [c.key, []]));

const ESTADOS_EN_ORDEN = [
  estadosConcurso.primeraRueda,
  estadosConcurso.segundaRueda,
  estadosConcurso.liguilla,
  estadosConcurso.nocheFallos,
  estadosConcurso.resultadosDisponibles
];

const tituloProximoEstado = estadoActual => {
  const proximo = ESTADOS_EN_ORDEN[ESTADOS_EN_ORDEN.indexOf(estadoActual) + 1];
  return proximo
    ? `Avanzar a ${estadoConcursoDescripcion(proximo)}`
    : "Ya tiene resultados disponibles";
};

// Mismo chequeo que usa la columna actionError, reusado acá para la barra de
// progreso: cuántos conjuntos hay cargados de cada categoría vs. cuántos se
// declararon.
const faltanConjuntos = row =>
  CATEGORIAS.some(
    c =>
      (row[`cantidad${capitalizar(c.key)}`] ?? 0) >
      (row.conjuntos?.filter(x => x.categoria === c.clave).length ?? 0)
  );

export const Concursos = () => {
  const [conjuntos, setConjuntos] = useState([]);
  const [Concursos, setConcursos] = useState([]);
  const [agregando, setAgregando] = useState(false);
  const [nombre, setNombre] = useState("");
  const [fechaHoraCierrePenca, setFechaHoraCierrePenca] = useState(new Date());
  const [editando, setEditando] = useState(false);
  const [idEditando, setIdEditando] = useState(0);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [cantidades, setCantidades] = useState(CANTIDADES_INICIALES);
  const [cantidadesLiguilla, setCantidadesLiguilla] = useState(
    CANTIDADES_LIGUILLA_INICIALES
  );
  const [conjuntosPorCategoria, setConjuntosPorCategoria] = useState(
    conjuntosVacios()
  );
  const [textoBusqueda, setTextoBusqueda] = useState("");
  const [cantidadEtapasPrimeraRueda, setCantidadEtapasPrimeraRueda] = useState(
    10
  );
  const [cantidadEtapasSegundaRueda, setCantidadEtapasSegundaRueda] = useState(
    10
  );
  const [cantidadEtapasLiguilla, setCantidadEtapasLiguilla] = useState(8);
  const sendRequest = useRequestHandler();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [validacion, setValidacion] = useState(null);

  const setCantidad = (key, value) =>
    setCantidades(prev => ({ ...prev, [key]: value }));
  const setCantidadLiguilla = (key, value) =>
    setCantidadesLiguilla(prev => ({ ...prev, [key]: value }));
  const setConjuntosCategoria = (key, value) =>
    setConjuntosPorCategoria(prev => ({ ...prev, [key]: value }));

  // Estado inconsistente con el dominio: guardar cantidadXLiguilla > cantidadX
  // no tiene sentido (no se puede clasificar a la liguilla más conjuntos de
  // los que van a concursar). QuantityStepper no conoce esta regla — se la
  // pasamos como error/helperText, y bloqueamos Guardar mientras exista.
  const categoriasConLiguillaInconsistente = CATEGORIAS.filter(
    c => cantidadesLiguilla[c.key] > cantidades[c.key]
  );

  const handleValidarTemporada = id => {
    sendRequest(Servicios.ValidarTemporada, [id], data => {
      setValidacion(data?.data);
    });
  };
  const refrescarGrilla = () => {
    sendRequest(Servicios.ObtenerConcursos, [], data => {
      setConcursos(data?.data);
    });
  };

  useEffect(() => {
    if (agregando || editando) {
      // excluirFueraDeConcurso=true: este selector es de ALTA (candidatos que
      // todavía no están en el concurso), a diferencia de otras pantallas que
      // consumen el mismo endpoint para listar quién ya está concursando. Sin
      // este flag, Conjunto/Concursan devolvía también conjuntos "fuera de
      // concurso" acá (ver REINGENIERIA_BACKOFFICE.md, bug confirmado).
      sendRequest(
        Servicios.ConjuntosConcursan,
        [textoBusqueda, idEditando, true],
        data => {
          const idsAgregados = CATEGORIAS.flatMap(
            c => conjuntosPorCategoria[c.key]
          ).map(x => x.id);
          setConjuntos(data?.data.filter(z => !idsAgregados.includes(z.id)));
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agregando, editando, textoBusqueda, conjuntosPorCategoria]);

  useEffect(() => {
    refrescarGrilla();
  }, []);

  const eliminarConcurso = id => {
    sendRequest(Servicios.EliminarConcurso, [id], refrescarGrilla);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 5 },
    { field: "nombre", headerName: "Nombre", flex: 20 },
    { field: "anio", headerName: "Año", flex: 10 },
    {
      field: "concursoEstadoId",
      headerName: "Estado",
      flex: 30,
      renderCell: params => (
        <BarraProgresoConcurso
          estadoActual={params.row.concursoEstadoId}
          tieneHallazgosBloqueantes={faltanConjuntos(params.row)}
        />
      )
    },
    {
      field: "action",
      headerName: "",
      sortable: false,
      flex: 5,
      align: "right",
      renderCell: params => {
        if (params.row.concursoEstadoId == estadosConcurso.segundaRueda)
          return (
            <>
              <FaFlagCheckered
                size={26}
                className="action-icon"
                onClick={() =>
                  navigate(`/liguilla/${params?.row?.id}`)
                }
                title={"Liguilla"}
              />
            </>
          );
        if (params.row.concursoEstadoId == estadosConcurso.nocheFallos)
          return (
            <>
              <GiPodium
                size={30}
                className="action-icon"
                onClick={() =>
                  navigate(`/resultados/${params?.row?.id}`)
                }
                title={"Resultados"}
              />
            </>
          );
      }
    },
    {
      field: "actionError",
      headerName: "",
      sortable: false,
      flex: 5,
      align: "right",
      renderCell: params => {
        return faltanConjuntos(params.row) ? (
          <BiErrorAlt
            color="red"
            size={28}
            className="action-icon"
            title="FALTAN CONJUNTOS"
            onClick={() => editarConcurso(params)}
          />
        ) : (
          <></>
        );
      }
    },
    {
      field: "actionValidar",
      headerName: "",
      sortable: false,
      minWidth: 5,
      align: "right",
      renderCell: params => {
        return (
          <BiCheckShield
            size={28}
            className="action-icon"
            title="Validar temporada"
            onClick={() => handleValidarTemporada(params?.row?.id)}
          />
        );
      }
    },
    {
      field: "actionEtapas",
      headerName: "",
      sortable: false,
      minWidth: 5,
      align: "right",
      renderCell: params => {
        const onClick = e => {
          navigate(`/etapas-concurso/${params?.row?.id}`);
        };

        return (
          <BiCalendarAlt
            size={28}
            className="action-icon"
            title="Etapas"
            onClick={onClick}
          />
        );
      }
    },
    {
      field: "actionEstado",
      headerName: "",
      sortable: false,
      minWidth: 5,
      align: "right",
      renderCell: params => {
        const onClick = e => {
          navigate(`/concurso/${params?.row?.id}/cambiarEstado`);
        };

        return (
          <FiSettings
            size={28}
            className="action-icon"
            title={tituloProximoEstado(params.row.concursoEstadoId)}
            onClick={onClick}
          />
        );
      }
    },
    {
      field: "actionEdit",
      headerName: "",
      sortable: false,
      minWidth: 10,
      align: "right",
      renderCell: params => {
        const onClick = e => {
          editarConcurso(params);
        };

        return <EditIcon className="action-icon" onClick={onClick} />;
      }
    },
    {
      field: "actionDelete",
      headerName: "",
      sortable: false,
      minWidth: 10,
      align: "right",
      renderCell: params => {
        const onClick = e => {
          if (window.confirm("¿Confirmás eliminar el Concurso?")) {
            eliminarConcurso(params?.row?.id);
          }
        };

        return <DeleteIcon className="action-icon" onClick={onClick} />;
      }
    }
  ];

  const editarConcurso = params => {
    setAgregando(true);
    setEditando(true);
    setNombre(params.row.nombre);
    setAnio(params.row.anio);
    setIdEditando(params.row.id);
    setConjuntosPorCategoria(
      Object.fromEntries(
        CATEGORIAS.map(c => [
          c.key,
          params.row.conjuntos.filter(x => x.categoria == c.clave)
        ])
      )
    );
    setCantidades(
      Object.fromEntries(
        CATEGORIAS.map(c => [
          c.key,
          params.row[`cantidad${capitalizar(c.key)}`]
        ])
      )
    );
    setCantidadesLiguilla(
      Object.fromEntries(
        CATEGORIAS.map(c => [
          c.key,
          params.row[`cantidad${capitalizar(c.key)}Liguilla`]
        ])
      )
    );
    setCantidadEtapasPrimeraRueda(params.row.cantidadEtapasPrimeraRueda);
    setCantidadEtapasSegundaRueda(params.row.cantidadEtapasSegundaRueda);
    setCantidadEtapasLiguilla(params.row.cantidadEtapasLiguilla);
    setFechaHoraCierrePenca(
      new Date(params.row.fechaHoraCierrePenca) ?? new Date()
    );
  };

  const columnsConjuntos = [
    { field: "nombre", headerName: "Nombre", flex: 99 },
    {
      field: "actionAdd",
      headerName: "",
      sortable: false,
      align: "right",
      flex: 1,
      renderCell: params => {
        const onClick = e => {
          const categoria = CATEGORIAS.find(
            c => c.clave === params?.row?.categoria
          );
          if (!categoria) {
            setTextoBusqueda("");
            return;
          }
          const actuales = conjuntosPorCategoria[categoria.key];
          if (actuales.length < cantidades[categoria.key]) {
            setConjuntosCategoria(categoria.key, [...actuales, params.row]);
          } else {
            dispatch(showErrorMessage("Máximo alcanzado"));
          }
          setTextoBusqueda("");
        };

        return <AddCircleOutline className="action-icon" onClick={onClick} />;
      }
    }
  ];

  const limpiarForm = () => {
    setNombre("");
    setAnio("");
    setCantidades(CANTIDADES_INICIALES);
    setCantidadesLiguilla(CANTIDADES_LIGUILLA_INICIALES);
    setConjuntosPorCategoria(conjuntosVacios());
    setConjuntos([]);
  };

  const handleGuardar = () => {
    const payloadCantidades = {};
    const payloadConjuntos = {};
    CATEGORIAS.forEach(c => {
      payloadCantidades[`cantidad${capitalizar(c.key)}`] = cantidades[c.key];
      payloadCantidades[`cantidad${capitalizar(c.key)}Liguilla`] =
        cantidadesLiguilla[c.key];
      payloadConjuntos[c.key] = conjuntosPorCategoria[c.key].map(x => x.id);
    });

    if (editando) {
      sendRequest(
        Servicios.ModificarConcurso,
        [
          idEditando,
          {
            id: idEditando,
            nombre,
            anio,
            ...payloadCantidades,
            cantidadEtapasPrimeraRueda,
            cantidadEtapasSegundaRueda,
            cantidadEtapasLiguilla,
            ...payloadConjuntos,
            fechaHoraCierrePenca: formatearFechaHoraYYYYMMDDHHMMSS(
              fechaHoraCierrePenca
            )
          }
        ],
        () => {
          limpiarForm();
          setEditando(false);
          setAgregando(false);
          dispatch(showSuccessMessage("Concurso modificado correctamente"));
          refrescarGrilla();
        }
      );
    } else {
      sendRequest(
        Servicios.CrearConcurso,
        [
          {
            nombre,
            anio,
            ...payloadCantidades,
            cantidadEtapasPrimeraRueda,
            cantidadEtapasSegundaRueda,
            cantidadEtapasLiguilla,
            ...payloadConjuntos,
            // Todo concurso nuevo arranca en Primera rueda — no es un campo
            // editable en este form (ver REINGENIERIA_BACKOFFICE.md: el motor
            // de estado es el único punto de escritura de ConcursoEstadoId a
            // partir de acá).
            concursoEstadoId: estadosConcurso.primeraRueda
          }
        ],
        () => {
          limpiarForm();
          setEditando(false);
          setAgregando(false);
          dispatch(showSuccessMessage("Concurso creado correctamente"));
          refrescarGrilla();
        }
      );
    }
  };

  return (
    <>
      <>
        {!agregando ? (
          <div className="card-modern">
            <div className="page-header">
              <h4 className="page-title">Listado de Concursos</h4>
              <div>
                <Button
                  variant={"contained"}
                  onClick={() => setAgregando(true)}
                >
                  Agregar
                </Button>
              </div>
            </div>
            <hr className="border-gray-200 dark:border-gray-700 my-4" />
            <div className="grid-wrapper">
              {Concursos?.length > 0 ? (
                <DataGrid
                  rows={Concursos}
                  columns={columns}
                  paginationModel={{ page: 0, pageSize: 50 }}
                  checkboxSelection={false}
                />
              ) : (
                <h5>No se encontraron resultados</h5>
              )}
            </div>
          </div>
        ) : (
          <div className="card-modern">
            <div className="page-header">
              <h4 className="page-title">
                {editando ? "Modificar Concurso" : "Nuevo Concurso"}
              </h4>
            </div>
            <hr className="border-gray-200 dark:border-gray-700 my-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h5 className="text-center">Datos generales</h5>
                <hr className="border-gray-200 dark:border-gray-700 my-4" />
                <div className="mb-3">
                  <TextField
                    className="w-full"
                    size="small"
                    label="Nombre"
                    value={nombre}
                    onChange={e => setNombre(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    className="w-full"
                    size="small"
                    label="Año"
                    value={anio}
                    onChange={e => setAnio(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <QuantityStepper
                    min={0}
                    label="Etapas en primera rueda"
                    value={cantidadEtapasPrimeraRueda}
                    onChange={setCantidadEtapasPrimeraRueda}
                  />
                </div>
                <div className="mb-3">
                  <QuantityStepper
                    min={0}
                    value={cantidadEtapasSegundaRueda}
                    onChange={setCantidadEtapasSegundaRueda}
                    label="Etapas en segunda rueda"
                  />
                </div>
                <div className="mb-3">
                  <QuantityStepper
                    min={0}
                    label="Etapas en liguilla"
                    value={cantidadEtapasLiguilla}
                    onChange={setCantidadEtapasLiguilla}
                  />
                </div>
                <div className="mb-3">
                  <hr className="border-gray-200 dark:border-gray-700 my-4" />
                  <h5>Cierre de pencas:</h5>
                  <br />
                  <DateField
                    value={fechaHoraCierrePenca}
                    onChange={setFechaHoraCierrePenca}
                    showTime
                    label="Cierre de pencas"
                  />
                  <hr className="border-gray-200 dark:border-gray-700 my-4" />
                </div>
              </div>

              <div className="text-end">
                <h5 className="text-center">Primera y segunda rueda</h5>
                <hr className="border-gray-200 dark:border-gray-700 my-4" />
                {CATEGORIAS.map(c => (
                  <div className="mb-3" key={c.key}>
                    <QuantityStepper
                      min={0}
                      className="w-1/2"
                      label={c.label}
                      value={cantidades[c.key]}
                      onChange={v => setCantidad(c.key, v)}
                    />
                  </div>
                ))}
              </div>
              <div className="text-center">
                <h5 className="text-center">Liguilla</h5>
                <hr className="border-gray-200 dark:border-gray-700 my-4" />
                {CATEGORIAS.map(c => {
                  const inconsistente =
                    cantidadesLiguilla[c.key] > cantidades[c.key];
                  return (
                    <div className="mb-3" key={c.key}>
                      <QuantityStepper
                        min={0}
                        className="w-1/2"
                        label={c.label}
                        value={cantidadesLiguilla[c.key]}
                        onChange={v => setCantidadLiguilla(c.key, v)}
                        error={inconsistente}
                        helperText={
                          inconsistente
                            ? `No puede superar a ${c.label} (${cantidades[c.key]})`
                            : undefined
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <hr className="border-gray-200 dark:border-gray-700 my-4" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <TextField
                  className="w-full my-1"
                  size="small"
                  label="Buscar conjunto"
                  value={textoBusqueda}
                  onChange={e => setTextoBusqueda(e.target.value)}
                />

                <div className="grid-wrapper" style={{ height: 400, overflow: "auto" }}>
                  <DataGrid
                    pagination={false}
                    disableColumnFilter
                    disableColumnMenu
                    disableDensitySelector
                    hideFooterPagination
                    rows={conjuntos}
                    columns={columnsConjuntos}
                    checkboxSelection={false}
                    className="no-header"
                  />
                </div>
              </div>
              {[CATEGORIAS.slice(0, 1), CATEGORIAS.slice(1, 3), CATEGORIAS.slice(3)].map(
                (grupo, i) => (
                  <div
                    className={i === 0 ? "text-center listado-conjunto-container" : "text-start"}
                    key={i}
                  >
                    {grupo.map(c => (
                      <div className="listado-conjunto-container" key={c.key}>
                        <h5>{`${c.label} (${conjuntosPorCategoria[c.key]?.length} de ${cantidades[c.key]})`}</h5>
                        <progress
                          className="w-3/4"
                          max={cantidades[c.key]}
                          value={conjuntosPorCategoria[c.key]?.length}
                        ></progress>
                        <ConjuntoConcursoListItem
                          conjuntosCategoria={conjuntosPorCategoria[c.key]}
                          conjuntos={conjuntos}
                          setterCategoria={v => setConjuntosCategoria(c.key, v)}
                          setConjuntos={setConjuntos}
                        ></ConjuntoConcursoListItem>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
            <hr className="border-gray-200 dark:border-gray-700 my-4" />
            <div className="flex justify-between w-full">
              <div className="text-start">
                <Button
                  variant={"outlined"}
                  onClick={() => {
                    setEditando(false);
                    setAgregando(false);
                    limpiarForm();
                  }}
                >
                  Cancelar
                </Button>
              </div>
              <div className="text-end">
                <Button
                  variant={"contained"}
                  onClick={handleGuardar}
                  disabled={categoriasConLiguillaInconsistente.length > 0}
                  title={
                    categoriasConLiguillaInconsistente.length > 0
                      ? `Revisá la cantidad de liguilla de: ${categoriasConLiguillaInconsistente
                          .map(c => c.label)
                          .join(", ")}`
                      : undefined
                  }
                >
                  Guardar
                </Button>
              </div>
            </div>
          </div>
        )}
      </>

      <Dialog
        open={!!validacion}
        onClose={() => setValidacion(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Validación de temporada
          {validacion?.anio ? ` — ${validacion.anio}` : ""}
        </DialogTitle>
        <DialogContent>
          <HallazgosValidacionList
            hallazgos={validacion?.hallazgos ?? []}
            cantidadErrores={validacion?.cantidadErrores ?? 0}
            cantidadAdvertencias={validacion?.cantidadAdvertencias ?? 0}
            mensajeVacio="La temporada está lista: no se encontraron problemas."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidacion(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withAuthenticationRequired(Concursos, {
  onRedirecting: () => <Loading />
});
